/**
 * COMPONENT: BedDeferrableGroup
 * Bloc d'informations critiques (passeport / permis / CEAM) avec l'échappatoire
 * du cahier L14 : « s'il ne connaît pas une information, il doit avoir la
 * possibilité de cocher une case comme quoi il s'engage à communiquer ces
 * éléments avant le dd/mm/aaaa ».
 *
 * Cocher la case remplace la saisie : les champs du groupe cessent d'être
 * exigés, l'étape redevient franchissable, mais elle est marquée ⚠ dans la
 * frise et l'élément apparaît dans l'en-tête du BED à chaque retour.
 */

import { CalendarClock } from 'lucide-react';
import type { ReactNode } from 'react';
import { BED_DEFERRAL_GROUP_LABELS, type BedDeferralGroup } from '../../lib/bed/wizard-model';

interface BedDeferrableGroupProps {
  group: BedDeferralGroup;
  /** Rôle concerné (`pilot` / `copilot` / `common`), pour l'identifiant DOM. */
  role: string;
  /** Report actif : date à laquelle le participant s'engage. */
  deferredUntil: string | null;
  /** Date proposée par défaut (deadline de l'édition). */
  defaultDueDate: string;
  onToggle: (deferred: boolean, dueDate: string) => void;
  onDueDateChange: (dueDate: string) => void;
  disabled?: boolean;
  children: ReactNode;
}

export function BedDeferrableGroup({
  group,
  role,
  deferredUntil,
  defaultDueDate,
  onToggle,
  onDueDateChange,
  disabled = false,
  children,
}: BedDeferrableGroupProps) {
  const isDeferred = deferredUntil !== null;
  const checkboxId = `defer-${group}-${role}`;
  const dateId = `defer-date-${group}-${role}`;

  return (
    <fieldset
      className={`space-y-3 rounded-lg border p-4 ${
        isDeferred ? 'border-amber-200 bg-amber-50/60' : 'border-zinc-200'
      }`}
    >
      <legend className="px-1 text-sm font-semibold text-zinc-900">
        {BED_DEFERRAL_GROUP_LABELS[group]}
      </legend>

      {!isDeferred && <div className="space-y-4">{children}</div>}

      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id={checkboxId}
            checked={isDeferred}
            disabled={disabled}
            onChange={(event) => onToggle(event.target.checked, defaultDueDate)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-amber-600 focus:ring-amber-500"
          />
          <label htmlFor={checkboxId} className="cursor-pointer text-sm text-zinc-700">
            Je ne connais pas encore cette information — je m’engage à la communiquer avant le
          </label>
        </div>

        {isDeferred && (
          <div className="flex flex-wrap items-center gap-2 pl-6">
            <CalendarClock className="h-4 w-4 shrink-0 text-amber-600" />
            <label htmlFor={dateId} className="sr-only">
              Date d’engagement pour {BED_DEFERRAL_GROUP_LABELS[group]}
            </label>
            <input
              type="date"
              id={dateId}
              value={deferredUntil ?? defaultDueDate}
              disabled={disabled}
              onChange={(event) => onDueDateChange(event.target.value)}
              className="h-9 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <span className="text-xs text-amber-700">
              Votre bulletin restera « incomplet » jusqu’à réception.
            </span>
          </div>
        )}
      </div>
    </fieldset>
  );
}
